import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LightingIcon from 'src/assets/icons/Lighting.svg';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { CLASSIFICATION_BADGE_CONFIG, getBadgeLabelByOrderStatus } from 'src/constants/common';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { BadgeType } from 'src/interface/common';
import { formatAddress, getFormattedDate, getFormattedNumber, getShortName, onError } from 'src/utils/CommonFunctions';

// Renders a moment-formatted date string that contains an ordinal suffix (e.g. "Feb 20th , 09:30 AM")
// as React elements with a proper <sup> tag — avoids dangerouslySetInnerHTML entirely.
const renderDateWithSuffix = (dateStr: string) => {
    const match = dateStr.match(/^(.*?)(\d+)(st|nd|rd|th)(.*)$/);

    if (!match) return <>{dateStr}</>;

    const [, before, day, suffix, after] = match;

    return (
        <>
            {before}
            {day}
            <sup>{suffix}</sup>
            {after}
        </>
    );
};

const OrderDraggableCell = ({ columnName, data }: { columnName: string; data: any }) => {
    const { t } = useTranslation();
    const orgBranding = useOrgBranding();
    const orderIdRef = useRef<HTMLDivElement>(null);
    const handlingUnitRef = useRef<HTMLParagraphElement>(null);
    const equipmentTypeRef = useRef<HTMLParagraphElement>(null);
    const salesRepNameRef = useRef<HTMLParagraphElement>(null);

    const orderStatusBadgeType = useMemo((): BadgeType => {
        const status = data?.orderStatus;

        if (status === 'draft' || status === 'cancelled' || status === 'dispatched') return 'pink';
        if (status === 'booked' || status === 'delivered') return 'lightblue';
        if (status === 'in-transit' || status === 'booking_failed') return 'error';
        if (status === 'completed') return 'gray';

        return 'success';
    }, [data?.orderStatus]);

    if (data === null || data === undefined) return <>-</>;

    const cellContent = () => {
        switch (columnName) {
            case 'Order #':
                return (
                    <div className="flex items-center gap-x-2 overflow-hidden">
                        <TruncateTextCmp refName={orderIdRef} message={`#${data?.orderId}`}>
                            <div ref={orderIdRef} className="text-utilityBlack800 font-semibold truncate">
                                #{data?.orderId}
                            </div>
                        </TruncateTextCmp>
                        {data?.isPriority && (
                            <TooltipCmp message={'Marked as Priority'}>
                                <img src={LightingIcon} className="w-3.5 h-3.5 flex-none" alt="Priority" />
                            </TooltipCmp>
                        )}
                    </div>
                );

            case 'Created By': {
                const repData = data?.userData;
                const fullName = `${repData?.firstName || ''} ${repData?.lastName || ''}`.trim();

                return fullName ? (
                    <div className="flex items-center gap-x-2 overflow-hidden">
                        {repData?.image ? (
                            <img className="table-profile-icn-circle-xs flex-none" src={repData.image} alt={repData.firstName || ''} onError={onError} />
                        ) : (
                            <div className="table-profile-user-circle-xs flex-none">{getShortName(fullName)}</div>
                        )}
                        <TruncateTextCmp refName={salesRepNameRef} message={fullName}>
                            <p ref={salesRepNameRef} className="truncate">
                                {fullName}
                            </p>
                        </TruncateTextCmp>
                    </div>
                ) : (
                    <>-</>
                );
            }

            case 'Status':
                return (
                    <BadgeCmp style="modern" type={orderStatusBadgeType}>
                        {data?.orderStatus ? getBadgeLabelByOrderStatus(data.orderStatus) : '-'}
                    </BadgeCmp>
                );

            case 'Type': {
                const config = CLASSIFICATION_BADGE_CONFIG[data?.orderType];

                return config ? (
                    <BadgeCmp style="modern" type={config.type}>
                        {t(config.label)}
                    </BadgeCmp>
                ) : null;
            }

            case 'Origin':
                return data?.originAddress?.city || data?.originAddress?.state ? (
                    <p className="truncate">
                        <TooltipCmp
                            message={formatAddress(
                                data?.originAddress?.postal
                                    ? `${data?.originAddress?.city}, ${data?.originAddress?.state}, ${data?.originAddress?.postal}`
                                    : `${data?.originAddress?.city}, ${data?.originAddress?.state}`
                            )}
                            parentClassName="w-full"
                        >
                            {formatAddress(`${data?.originAddress?.city}, ${data?.originAddress?.state}`)}
                        </TooltipCmp>
                    </p>
                ) : (
                    <>-</>
                );

            case 'Destination':
                return data?.destinationAddress?.city && data?.destinationAddress?.state ? (
                    <p className="truncate">
                        <TooltipCmp
                            message={formatAddress(
                                data?.destinationAddress?.postal
                                    ? `${data?.destinationAddress?.city}, ${data?.destinationAddress?.state}, ${data?.destinationAddress?.postal}`
                                    : `${data?.destinationAddress?.city}, ${data?.destinationAddress?.state}`
                            )}
                            parentClassName="w-full"
                        >
                            {formatAddress(`${data?.destinationAddress?.city}, ${data?.destinationAddress?.state}`)}
                        </TooltipCmp>
                    </p>
                ) : (
                    <>-</>
                );

            case 'Created':
                return <span>{data?.createdAt ? renderDateWithSuffix(getFormattedDate(data?.createdAt, `MMM Do, hh:mm A`, true, true)) : '-'}</span>;

            case 'Last Modified':
                return <span>{data?.updatedAt ? renderDateWithSuffix(getFormattedDate(data?.updatedAt, `MMM Do, hh:mm A`, true, true)) : '-'}</span>;

            case 'Handling Unit':
                return (
                    <>
                        {data?.orderType === 'ltl' && data?.handlingUnitTypes ? (
                            <TruncateTextCmp refName={handlingUnitRef} message={data?.handlingUnitTypes}>
                                <p ref={handlingUnitRef} className="truncate max-w-[120px]">
                                    {data?.handlingUnitTypes}
                                </p>
                            </TruncateTextCmp>
                        ) : (
                            <TruncateTextCmp refName={equipmentTypeRef} message={data?.primaryEquipmentTypeFullName || data?.handlingUnits || '-'}>
                                <p ref={equipmentTypeRef} className="max-w-[120px] truncate">
                                    {data?.primaryEquipmentTypeFullName || data?.handlingUnits || '-'}
                                </p>
                            </TruncateTextCmp>
                        )}
                    </>
                );

            case 'Total Weight':
                return <>{data?.totalWeight ? <span>{`${getFormattedNumber(data?.totalWeight, false)} ${data?.weightMeasurement ? data.weightMeasurement.toLowerCase() : 'lbs'}`}</span> : <>-</>}</>;

            case 'Cost':
                return <>{data?.totalCost ? <span>{`$${getFormattedNumber(data?.totalCost, false)} ${data?.currency || 'CAD'}`}</span> : <>-</>}</>;

            case 'Pickup Date':
                return <span>{data?.pickupDate ? renderDateWithSuffix(getFormattedDate(data.pickupDate, 'MMM Do, YYYY', false, false)) : '-'}</span>;

            case 'Delivery Date':
                return <span>{data?.deliveryDate ? renderDateWithSuffix(getFormattedDate(data.deliveryDate, 'MMM Do, YYYY', false, false)) : '-'}</span>;

            case 'Carrier': {
                const carrier = data?.carrierData;
                const carrierRef = React.createRef<HTMLParagraphElement>();
                // Row presents the org itself as the carrier — no carrier, masked carrier (BE sends the
                // org name), or the legacy hardcoded name from older cached responses.
                const isOrgCarrier = !carrier || carrier.isNoCarrier || carrier.name === orgBranding.name || carrier.name === 'Western Alliance';
                const showOrgLogo = !carrier?.image && isOrgCarrier;

                return carrier ? (
                    <div className="flex items-center gap-x-2 overflow-hidden">
                        {carrier.image ? (
                            <img className="table-profile-customer-circle-xs rounded-md flex-none" src={carrier.image} alt={carrier.name} onError={onError} />
                        ) : showOrgLogo ? (
                            orgBranding.logoSrc ? (
                                <img className="table-profile-customer-circle-xs rounded-md flex-none object-contain" src={orgBranding.logoSrc} alt={orgBranding.name} onError={onError} />
                            ) : (
                                <div className="table-profile-customer-circle-xs rounded-md bg-utilityBlack flex items-center justify-center p-0.5 flex-none">
                                    <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                                </div>
                            )
                        ) : (
                            <div className="table-profile-customer-circle-xs !rounded-md flex-none">{getShortName(carrier.name || '')}</div>
                        )}
                        <TruncateTextCmp refName={carrierRef} message={carrier.name || orgBranding.name}>
                            <p ref={carrierRef} className="truncate">
                                {carrier.name || orgBranding.name}
                            </p>
                        </TruncateTextCmp>
                    </div>
                ) : (
                    <>-</>
                );
            }

            case 'BOL #':
                return <span>{data?.bolNumber || '-'}</span>;

            case 'Reference #':
                return <span>{data?.refNumber || '-'}</span>;

            case 'Linear Feet':
                return <span>{data?.linearFootage ? `${data.linearFootage} Ft` : '-'}</span>;

            case 'Equipment':
                return (
                    <TruncateTextCmp refName={equipmentTypeRef} message={data?.equipmentTypeFullName || data?.equipmentType || '-'}>
                        <p ref={equipmentTypeRef} className="truncate">
                            {data?.equipmentTypeFullName || data?.equipmentType || '-'}
                        </p>
                    </TruncateTextCmp>
                );

            default:
                return <>-</>;
        }
    };

    return <>{cellContent()}</>;
};

export default OrderDraggableCell;
